import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Grid,
  Divider,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AdminDashboard from "../../Admin/Dashboard/Dashboard";
import './NewLaunchAd.css';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`https://landnest.net:81/ad-property/${id}/`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setPropertyData(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch property data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not Set';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPropertyName = () => {
    if (propertyData?.property_name) {
      return propertyData.property_name;
    }
    return `${propertyData?.category_name || 'Property'} ${propertyData?.property_id || ''}`;
  };

  const getBHKInfo = () => {
    if (propertyData?.bedrooms_count) {
      return `${propertyData.bedrooms_count} BHK`;
    }
    if (propertyData?._1bhk_count) return `${propertyData._1bhk_count} BHK`;
    if (propertyData?._2bhk_count) return `${propertyData._2bhk_count} BHK`;
    if (propertyData?._3bhk_count) return `${propertyData._3bhk_count} BHK`;
    if (propertyData?._4bhk_count) return `${propertyData._4bhk_count} BHK`;
    return 'N/A';
  };

  const getAreaRange = () => {
    if (propertyData?.min_acres && propertyData?.max_acres) {
      return `${propertyData.min_acres} - ${propertyData.max_acres} acres`;
    }
    if (propertyData?.site_area) {
      return `${propertyData.site_area} ${propertyData.units || 'sq ft'}`;
    }
    return 'N/A';
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4 }}>
        <AdminDashboard />
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading property details...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ mt: 4 }}>
        <AdminDashboard />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6">Error: {error}</Typography>
          <Button variant="contained" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!propertyData) {
    return (
      <Container maxWidth={false} sx={{ mt: 4 }}>
        <AdminDashboard />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No property data available</Typography>
          <Button variant="contained" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4 }}>
      <AdminDashboard />
      
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 2 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 3 }}
          variant="outlined"
        >
          Back to Properties
        </Button>

        {/* Property Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
                {getPropertyName()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Submitted by {propertyData.user_first_name || 'N/A'} {propertyData.user_last_name || ''}
                {' '}on {formatDate(propertyData.created_at)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {propertyData.type && (
                <Chip
                  label={propertyData.type.replace('-', ' ').toUpperCase()}
                  color="success"
                  size="medium"
                />
              )}
              <Chip label="Featured" color="warning" />
              <Chip label="Recommended" color="info" />
              <Chip
                label={propertyData.Admin_status || 'Pending'}
                color={
                  propertyData.Admin_status?.toLowerCase() === 'approved' ? 'success' :
                  propertyData.Admin_status?.toLowerCase() === 'rejected' ? 'error' : 'warning'
                }
              />
            </Box>
          </Box>
        </Paper>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Left Column - Property Details */}
          <Grid item xs={12} lg={8}>
            {/* Submitted Property Information */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Submitted Property Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Project Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#1a237e' }}>
                    {getPropertyName()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Property Type
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.category_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Locality
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.location || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Latitude
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.lat || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Longitude
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.long || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    BVE
                  </Typography>
                  <Typography variant="body1">
                    {getBHKInfo()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Pricing
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.2rem' }}>
                    {formatPrice(propertyData.price)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Contact Details
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a237e' }}>
                    {propertyData.mobile_no || propertyData.user_mobile_no || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Additional Information */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Builders
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.posted_by || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.user_email || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Assessed
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.location || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Land Size
                  </Typography>
                  <Typography variant="body1">
                    {getAreaRange()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Area (L × W)
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.length || 'N/A'} × {propertyData.width || 'N/A'} {propertyData.units || ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Facing
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.facing || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Bathrooms
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.bathrooms_count || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Road Width
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.roadwidth || 'N/A'} {propertyData.units || ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Power Backup
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.power_backup || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Gated Security
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.gated_security || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Parking
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.parking || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Lift
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.lift || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Requested Category
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {propertyData.type && (
                      <Chip label={propertyData.type.replace('-', ' ').toUpperCase()} color="success" size="small" />
                    )}
                    <Chip label="Featured" color="warning" size="small" />
                    <Chip label="Recommended" color="info" size="small" />
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Description */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Description
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {propertyData.description || 'No description available'}
              </Typography>
            </Paper>

            {/* Meta Information */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Meta Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Property ID
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.property_id || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(propertyData.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Updated At
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(propertyData.updated_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    User ID
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.user_id || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Category ID
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.category_id || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Status
                  </Typography>
                  <Chip
                    label={propertyData.status ? 'Active' : 'Inactive'}
                    color={propertyData.status ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Ad Start Date
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.adstart_date ? formatDate(propertyData.adstart_date) : 'Not Set'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Ad End Date
                  </Typography>
                  <Typography variant="body1">
                    {propertyData.adend_date ? formatDate(propertyData.adend_date) : 'Not Set'}
                  </Typography>
                </Grid>
                {propertyData.boost_date && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      Boost Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(propertyData.boost_date)}
                    </Typography>
                  </Grid>
                )}
                {propertyData.advance_payment && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      Advance Payment
                    </Typography>
                    <Typography variant="body1">
                      {formatPrice(propertyData.advance_payment)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Right Column - Property Image & Quick Info */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Property Image
              </Typography>
              <Box sx={{ 
                width: '100%',
                height: 300,
                bgcolor: '#f5f5f5',
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {propertyData.AdProperty_images && propertyData.AdProperty_images.length > 0 ? (
                  <img
                    src={`https://landnest.net:81${propertyData.AdProperty_images[0].image}`}
                    alt="Property"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<span style="color: #999;">No Image Available</span>';
                    }}
                  />
                ) : (
                  <Typography color="text.secondary">No Image Available</Typography>
                )}
              </Box>
            </Paper>

            {/* Quick Stats */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Quick Stats
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Price</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    {formatPrice(propertyData.price)}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Category</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {propertyData.category_name || 'N/A'}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Type</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {propertyData.type ? propertyData.type.replace('-', ' ').toUpperCase() : 'N/A'}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Facing</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {propertyData.facing || 'N/A'}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Bathrooms</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {propertyData.bathrooms_count || 'N/A'}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Site Area</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {propertyData.site_area || 'N/A'} {propertyData.units || ''}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default PropertyDetail;